/**
 * THE ONE FILE IN src/voyage THAT IMPORTS FROM src/sim.
 *
 * Round 1 shipped a voyage layer that imported nothing outside its own directory and
 * was, as a direct consequence, paced against a number the simulation had never heard
 * of - by a factor of 64. The isolation was real and the wiring was absent, and no test
 * in either lane could see the gap because every counterparty in every test was a stub
 * of the same placeholder.
 *
 * So the isolation is now stated as a rule with exactly one hole, and the hole is this
 * file. no-steering.test.ts enumerates it by name: every other source file in this
 * directory still imports only from './', and a second file reaching outside fails the
 * scan. One seam is auditable. Zero seams was a lie by omission.
 *
 * WHAT THIS FILE DOES NOT DO. It does not import step.ts, state.ts or time.ts directly.
 * It imports the simulation's PRODUCTION BARREL, src/sim/index.ts, so the voyage layer
 * is wired to the surface the rest of the game is allowed to call and cannot reach a
 * private helper that the sim lane is free to change.
 *
 * -- THE SCALAR ---------------------------------------------------------------
 *
 * Progression, for the purpose of where the boat is, is XP. It is the one quantity in
 * SimState that is monotone non-decreasing for the lifetime of a save, never spent, and
 * never reset. Coins are spendable and would un-discover places the moment an economy
 * existed. voyage.ts states monotonicity as the contract it cannot itself enforce; this
 * is where the choice that honours it is made.
 *
 * -- TWO CURVES, AND WHY THEY DIFFER PAST THE HOLD -----------------------------
 *
 * `simProgressionCurve` models CONTINUOUS PLAY: it advances the production tick function
 * over every tick, so hour 300 has been paid for every tick up to hour 300. That is the
 * right basis for "how many hours of play is this span of the sea", which is what the
 * pace is solved against.
 *
 * `simProgressSource` models a REAL SAVE being settled from timestamps: it calls the
 * production `advance` from a persisted state, which pays the first OFFLINE_HOLD_HOURS
 * of an absence in full and SKIPS the rest, per CONTRACT.md section 6.
 *
 * Inside the hold the two are identical, tick for tick, and sim-bridge.test.ts asserts
 * that on the production functions. Past the hold they diverge, by design and by the
 * owner's ruling: time beyond the hold is skipped and disclosed, never paid at a reduced
 * rate. `holdDivergenceHours` reports where that happens rather than hiding it.
 */

import {
  CURSOR_CEILING,
  MAX_COUNT,
  MAX_XP,
  OFFLINE_HOLD_HOURS,
  OFFLINE_HOLD_TICKS,
  TICK_MS,
  advance,
  anchorPaidTicks,
  createState,
  exportSave,
  msAtTick,
  normaliseEnvelope,
  openSession,
  previewAbsence,
  stepTick,
  tickAtMs,
  type AdvanceReport,
  type ImportResult,
  type SaveEnvelope,
  type SaveOutcome,
  type SaveStorage,
  type Session,
  type SimState,
} from '../sim/index';
import type { ProgressionCurve } from './pacing';
import type { Millis, Progress } from './units';
import type { CreateVoyageWorldOptions, VoyageWorld } from './world';
import { createVoyageWorld } from './world';

/**
 * THE SIMULATION SURFACE THE REST OF THIS DIRECTORY MAY USE, RE-EXPORTED.
 *
 * no-steering.test.ts enforces that exactly ONE file here reaches outside src/voyage, and
 * this is that file. `session.ts` and `persistence.ts` need the save lifecycle, so they
 * get it from here rather than by opening a second hole in the wall. Nothing is renamed
 * and nothing is wrapped: these are sim-core's own exports, and a caller that reads them
 * is reading the production barrel one hop away.
 */
export type { AdvanceReport, ImportResult, SaveEnvelope, SaveOutcome, SaveStorage, Session, SimState };
export { exportSave, normaliseEnvelope, openSession };
/**
 * THE HOLD, AND THE COUNT OF WHAT IT SKIPPED, re-exported so a player-facing surface can
 * say so. CONTRACT.md section 6 requires time past the hold to be "skipped AND DISCLOSED";
 * round 3 computed the skip correctly in sim-core, persisted it, and gave the voyage
 * layer's own view type no field to carry it, so the only reader in the repository was the
 * dev harness (CL3-C-04). `previewAbsence` is the piece that makes the disclosure
 * PRESENCE-INVARIANT: it reports what an absence WOULD skip without paying it, so
 * `view()` and `tick()` can answer identically. See session.ts.
 */
export { CURSOR_CEILING, MAX_COUNT, MAX_XP, OFFLINE_HOLD_HOURS, previewAbsence, TICK_MS };

/**
 * TICKS THE ANCHOR STATE HAS BEEN PAID FOR, or null when the save can no longer say.
 *
 * -- THE DEFECT THIS REPLACES, ROUND 6 --------------------------------------------
 *
 * Round 5's clock was `state.tick + min(gap, hold)`. The clamp bounds the CURRENT absence
 * correctly; what it was added to was wrong. src/sim/step.ts writes `tick: target` on any
 * skip, so the cursor is ELAPSED time - it has already absorbed every earlier skipped span
 * - and src/sim/hold.test.ts asserts exactly that (one tick over the hold: paid 86,400,
 * skipped 1, tick 86,401). Nothing in production subtracted `skippedTicks`; it was read for
 * the HUD disclosure and nowhere else. MEASURED through openVoyageSession on seed 4242:
 * player A, 60 hourly sessions, and player B, 36 hourly sessions then one 48-hour break,
 * have BYTE-IDENTICAL earned state (xp 655,124, coins 934,401, same catches) and were 556.5
 * leagues apart in a sea of radius 672.75 - A seaward, B homeward. The two documented
 * properties of this clock - "not elapsed wall-clock time" and "the hold's disclosure and
 * the boat's position tell the same story" - were false from the first skipped tick, and
 * CONTRACT.md section 6's PRESENCE CHANGES NOTHING was broken by the fix meant to keep it.
 *
 * The suite was green because deep-time.test.ts ticks at exactly 24-hour intervals - the
 * one cadence at which `splitAbsence` skips nothing - and session.test.ts's presence
 * check runs six hours, inside the hold. paid-clock.test.ts now drives cadences that are
 * NOT multiples of the hold.
 *
 * -- THE CORRECTION, AND WHY IT IS NOT JUST A SUBTRACTION -----------------------
 *
 * By construction `tick = paid + skipped`: every paid tick moves the cursor by one
 * (stepTick) and every skip moves it by the skip in the same write that banks the skip
 * (advanceWithReport). So `tick - skippedTicks` is paid time EXACTLY - while both terms
 * are exact. `tick` is: advance refuses at CURSOR_CEILING rather than writing it. But
 * `skippedTicks` is written through `addCapped(..., MAX_COUNT)` and SATURATES at
 * MAX_COUNT = 1e12 ticks = 277,777,777.8 hours = 31,688.1 years of skipped time. Past
 * that the counter stops and the cursor does not, so `tick - skippedTicks` would grow with
 * elapsed time again - the same class, one field over, and silent.
 *
 * IS THE SATURATION REACHABLE? Not by honest play: it needs 31,688 years of wall-clock
 * from the save's epoch. But MAX_COUNT (1e12) is BELOW CURSOR_CEILING (2^40 = 1.0995e12),
 * so the band [1e12, 2^40) where the counter is stuck and the cursor still moves exists,
 * 99,511,627,776 ticks = 27,642,118.8 hours wide. And the save door admits an `epochMs` as
 * low as -8.64e15, which puts today's Date.now() 1.0018e12 ticks past an imported epoch:
 * one import string reaches the band today. Not unreachable, so the correction is carried
 * differently: when the counter is at or past MAX_COUNT the history is unrecoverable and
 * this returns null - the clock REFUSES rather than guessing, and paidTicksAt publishes 0
 * for every timestamp, which is presence-invariant and cannot grow with elapsed time. The
 * boat lies at the outer ground and the hold view discloses 277 million skipped hours,
 * which is the honest state of such a save. The permanent fix belongs to sim-core:
 * MAX_COUNT >= CURSOR_CEILING makes the band empty, or a persisted paid-ticks field makes
 * this derivation unnecessary. paid-clock.test.ts asserts the band and the refusal.
 *
 * The `max(0, ...)` is for the loader, which bounds `skippedTicks` at MAX_EARNED = 2^52
 * and `tick` at 2^40, so a hostile save can carry more skipped than elapsed. Applied to the
 * whole sum in paidTicksAt, never to the anchor alone, so view() and tick() still agree.
 */
export { anchorPaidTicks };

/**
 * THE CONSTANT THAT KILLED THE BOAT THE SECOND TIME, re-exported so the voyage layer's own
 * fences can name it rather than re-type it.
 *
 * `MAX_XP` = 200,000,000 is RuneScape's experience ceiling, reused because the curve is.
 * src/sim/step.ts pays XP through `addCapped(state.xp, pace.xp, MAX_XP)` and
 * src/sim/never-nerf.test.ts asserts a save at the cap earns nothing more. It is CORRECT
 * and it is not a defect to be lifted.
 *
 * It is published here because the voyage layer has to know that `progressAt` is a BOUNDED
 * quantity. Round 5 spent a whole fix making the progression curve's inverse total before
 * noticing that the inverse's ARGUMENT saturates, at which point the settled round went
 * constant again - at (0, 0), from day 353 to for ever. A layer that paces anything by
 * progression needs that fact in front of it, not two modules away.
 */

/**
 * THE DEEPEST MODELLED HOUR ANY SAVE CAN EVER REACH. DERIVED, NEVER CHOSEN.
 *
 * This exists because round 4's freeze was a bound picked against a MILESTONE (level 99
 * at 742 h) in a game whose contract says the interesting hours start after that
 * milestone, and no test in this lane looked past hour 795.47. The answer to "how deep
 * should a horizon test go" cannot be another number somebody liked; it has to be the
 * number past which there is nothing to look at.
 *
 * `CURSOR_CEILING` is sim-core's own tightest cursor ceiling - `min(MAX_TICK,
 * MAX_SAVE_TICK)` = 2^40 ticks - and `isAdvanceable` refuses at it, so no save can be
 * carried to a tick index above it by any route. One tick is `TICK_MS`, so that ceiling
 * is a wall-clock bound, and continuous play is the upper bound on what any settled save
 * can have earned in that time. Modelled hours are the inverse of the continuous-play
 * curve, so they cannot exceed it either.
 *
 * 305,419,896.6 h is therefore 34,865 years of unbroken play, and it is not a big round
 * number: it is 2^40 seconds expressed in hours. A fence at this horizon cannot be
 * embarrassed by something further out, because the simulation has no further out.
 */
export const MAX_MODELLED_HOURS: number = (CURSOR_CEILING * TICK_MS) / 3_600_000;

/** How the simulation's state is turned into the one scalar the voyage layer reads. */
export function progressScalar(state: SimState): Progress {
  return state.xp;
}

export interface SimCurveOptions {
  /**
   * THE SAVE'S SEED. REQUIRED, AND THERE IS DELIBERATELY NO DEFAULT.
   *
   * CL2-R2-F: round 2 wrote `options.seed ?? 1` here while `simProgressSource` took the
   * seed from the save, and no test ever paired them - so the curve-mismatch guard was
   * only ever observed passing, and the one pairing the shipped singleton would actually
   * be handed was the one that threw. The dev harness hit it on its first real save.
   *
   * A default that is wrong for every real save is a trap, so it is gone rather than
   * corrected: every caller of every curve function in this file now has to say which
   * save it is talking about, and `tsc` is what enforces it.
   */
  readonly seed: number;
  /** Persisted skill. A save attribute, never a live input - CONTRACT.md section 6. */
  readonly skill?: number;
  /** '' means "the best species unlocked", which is what a fresh save carries. */
  readonly targetSpecies?: string;
  /**
   * Hours the curve is MEASURED over, tick by tick. It is built lazily up to whatever is
   * asked for and never past this, so a runaway query cannot hang.
   *
   * IT IS A COST BUDGET, NOT A DOMAIN. Past it the curve is carried at its measured
   * terminal rate, so both directions stay total, monotone and unbounded. Round 4 treated
   * this number as the edge of the world and returned it as an answer; see
   * `DEFAULT_SIM_CURVE_HORIZON_HOURS`.
   */
  readonly horizonHours?: number;
}

/** Ticks per hour. One tick is TICK_MS, so this is a fact about the simulation. */
const TICKS_PER_HOUR = Math.round(3_600_000 / TICK_MS);

/**
 * Hours a simulation curve is MEASURED over, tick by tick, unless a caller says otherwise.
 *
 * -- WHAT THIS NUMBER IS NOT, AS OF ROUND 5 -----------------------------------
 *
 * It is not the edge of the world, and the version of this comment that treated it as one
 * is what CL4-C-01 cost. Round 4 wrote here that the horizon "HAS TO COVER THE GAME'S
 * LIFETIME" and that `hoursAtProgress` CLAMPS at it - both true, and together they made a
 * dead world: at modelled hour 800.000 the inverse began returning 800, every consumer
 * downstream became constant, and the boat froze for ever at the farthest point from home.
 * Reachable in 34 days of once-a-day play. Nothing threw and no test went red, because the
 * fence was `800 > 742.31` - a horizon compared against the LEVEL 99 MILESTONE, in a game
 * whose CONTRACT.md 1.5d says the hundreds of hours come AFTER that milestone.
 *
 * The wall is gone rather than moved. Past this many hours the curve is carried at its own
 * measured terminal rate (`TAIL_RATE_WINDOW_HOURS`), in BOTH directions, so the pair stays
 * total, monotone, unbounded and mutually inverse. Raising 800 to 8,000 would have moved
 * the wall; deep-time.test.ts asserts the boat still sails at hour 1e6 on a curve measured
 * only to hour 100, which is the assertion a bigger constant cannot pass.
 *
 * SO WHAT DOES 800 STILL DO? It buys accuracy, and nothing else. The curve accelerates for
 * roughly its first 900 hours and then plateaus, so a table that stops inside the climb
 * carries the rest of the game at a rate that is too low. Measuring to 800 h puts the tail
 * window in the plateau: extrapolating from there to hour 1,200 lands within 1% of a table
 * actually built to 1,200, which deep-time.test.ts measures rather than asserts. Cost is
 * paid lazily and once per world - MEASURED at 612 ms to reach hour 800 on this machine -
 * and worlds are memoised by curve id.
 *
 * tools/lanes/core-loop/lifetime.test.ts still asserts this exceeds the derived lifetime.
 * That assertion is now about ACCURACY, not about liveness, and it is no longer the thing
 * standing between a player and a dead sea.
 */
export const DEFAULT_SIM_CURVE_HORIZON_HOURS = 800;

/**
 * Hours of the table's tail the TERMINAL RATE is measured over, for carrying the curve
 * past what was measured. MEASURED, and it is the minimum of a U rather than a number
 * somebody liked.
 *
 * Swept over windows {1, 2, 3, 6, 12, 24, 36, 48, 72, 120} h and seeds {1, 7, 42, 99,
 * 777, 2026, 4242, 31337}, comparing each window's estimate at hour 800 against the rate
 * actually measured from 800 to 1,200 h. Worst-case relative error across the eight
 * seeds:
 *
 *   1 h  2.695%   <- too short: one hour is a handful of casts and the RNG dominates
 *   6 h  0.477%
 *   24 h 0.392%
 *   36 h 0.321%   <- the floor
 *   48 h 0.435%
 *   120 h 4.611%  <- too long: the window reaches back into the acceleration, which is
 *                    still running at hour 700 and only plateaus around hour 900
 *
 * Deliberately NOT `OFFLINE_HOLD_HOURS`, which is 24 and would have read as the same
 * number twice. This one is derived from the curve's own shape and nothing else, and
 * deep-time.test.ts asserts it beats both arms of the U so an edit cannot slide it onto
 * either one.
 */
export const TAIL_RATE_WINDOW_HOURS = 36;

/**
 * A TABLE OF THE PROGRESSION AFTER n TICKS REPLAYED FROM `base`. Grown a tick at a time,
 * only as far as it is asked to go, and never rebuilt.
 *
 * `stepTick` is a pure function of (state, index) and `advance` runs consecutive indices
 * from the cursor, so a run that never skipped IS this table read at its own tick index -
 * in both directions. That is the whole of the rewind below, and it is the same fact the
 * progression curve is built on, which is why there is one table rather than two.
 */
interface ReplayTable {
  /** The raw table, so the curve's binary search can index it without a call per probe. */
  readonly xpAfter: number[];
  grow(ticks: number): void;
  at(ticks: number): Progress;
}

function replayFrom(base: SimState): ReplayTable {
  const xpAfter: number[] = [progressScalar(base)];
  let cursor: SimState = base;
  function grow(ticks: number): void {
    const want = Math.max(0, Math.ceil(ticks));
    while (xpAfter.length <= want) {
      cursor = stepTick(cursor, cursor.tick);
      xpAfter.push(progressScalar(cursor));
    }
  }
  return {
    xpAfter,
    grow,
    at(ticks: number): Progress {
      const n = Math.max(0, Math.floor(ticks));
      grow(n);
      return xpAfter[n] ?? progressScalar(base);
    },
  };
}

/** Most distinct curves one page will ever replay. Past this the oldest table is dropped. */
const MAX_MEMOISED_REPLAYS = 8;
const replaysByCurveId = new Map<string, ReplayTable>();

/**
 * THE CONTINUOUS-PLAY TABLE FOR ONE CURVE IDENTITY, memoised and shared.
 *
 * Two callers need the identical sequence: `simProgressionCurve`, which measures the
 * curve off it, and the rewind in `simProgressSource`, which reads a save's own past off
 * it. Building it twice would be two rate engines and 23 MB of duplicate doubles at the
 * default horizon, so it is built once per identity and handed to both.
 */
function continuousReplay(curveId: string, seed: number, skill: number, targetSpecies: string): ReplayTable {
  const hit = replaysByCurveId.get(curveId);
  if (hit !== undefined) return hit;
  const built = replayFrom({ ...createState(seed, 0), skill, targetSpecies });
  if (replaysByCurveId.size >= MAX_MEMOISED_REPLAYS) {
    const oldest = replaysByCurveId.keys().next();
    if (!oldest.done) replaysByCurveId.delete(oldest.value);
  }
  replaysByCurveId.set(curveId, built);
  return built;
}

/**
 * The real progression curve, MEASURED off the simulation by running its production tick
 * function. Nothing here is a stand-in and nothing here is a literal rate.
 *
 * Cost: the table is grown a tick at a time, only as far as it is asked to go, and
 * cached. Reaching hour 30 costs about 108,000 stepTick calls, measured at roughly 25 ms
 * on the development machine. Both directions are then O(1) and O(log n).
 *
 * THE TABLE IS SHARED WITH THE REWIND, so another caller may have grown it PAST this
 * curve's own horizon. Every read below is therefore taken at `measuredEnd()` rather than
 * at the array's length: a curve whose answers moved because something else asked for a
 * deeper tick first would be a function of what a caller did before it, which is exactly
 * what `tailRatePerTick`'s own docstring forbids.
 */
export function simProgressionCurve(options: SimCurveOptions): ProgressionCurve {
  const seed = options.seed;
  const skill = options.skill ?? 0;
  const targetSpecies = options.targetSpecies ?? '';
  const horizonHours = options.horizonHours ?? DEFAULT_SIM_CURVE_HORIZON_HOURS;
  const maxTicks = Math.max(0, Math.round(horizonHours * TICKS_PER_HOUR));
  // The measured horizon in hours, recovered from the tick count rather than from the
  // argument, so the two directions meet at exactly the same point and invert exactly.
  const measuredHours = maxTicks / TICKS_PER_HOUR;
  const curveId = `sim-xp:seed=${seed}:skill=${skill}:species=${targetSpecies === '' ? 'best' : targetSpecies}`;

  // xpAfter[n] is the progression a continuously-present player holds after n ticks.
  // xpAfter[0] = 0 by construction: a fresh save has earned nothing.
  const shared = continuousReplay(curveId, seed, skill, targetSpecies);
  const xpAfter = shared.xpAfter;

  function ensureTicks(n: number): void {
    shared.grow(Math.min(Math.max(0, Math.ceil(n)), maxTicks));
  }

  /**
   * The deepest index THIS curve will read, which is its own horizon and not the shared
   * table's length. See the header: the table is shared, so its length is not this
   * curve's business and reading it would make these answers order-dependent.
   */
  function measuredEnd(): number {
    return Math.min(xpAfter.length - 1, maxTicks);
  }

  /**
   * THE RATE THE CURVE IS CARRIED AT PAST WHAT WAS MEASURED. Measured off the end of the
   * table, never assumed, and computed once.
   *
   * This is the whole of the CL4-C-01 fix. The table is finite because a table covering
   * every hour a save can reach is 2^40 entries; the GAME is not finite, so the answer
   * past the table has to be a law rather than a bound. It is deterministic - the table
   * is always grown to exactly `maxTicks` before this is read, so the window never
   * depends on what a caller happened to ask for first - and it is monotone, so the
   * inverse stays an inverse.
   *
   * SIZE AND SIGN OF ITS ERROR, and the sign is NOT what this comment first claimed. It
   * said the curve is still accelerating where the window sits, so the carried rate must
   * be a LOWER bound and the hours an UPPER bound. Measured, that is false: on seed 4242
   * the 36-hour window comes out 0.29% HIGH, and a real session at day 100 inverts to
   * 2,398.58 h against 2,400 h actually paid - the hours are LOW, the opposite direction.
   * The error is signed and seed-dependent, bounded at 0.321% of rate across eight seeds,
   * and it is not a one-sided bound. Corrected here rather than quietly dropped, because a
   * direction asserted without measurement is exactly the habit this run keeps paying for.
   *
   * This is also why the settled round no longer uses this function as its clock: an
   * estimate good to three parts in a thousand is fine for pacing a chart readout and is
   * the wrong thing to derive a position from when an exact cursor is sitting right there.
   * See `ProgressSource.paidHoursAt` in voyage.ts.
   *
   * IT REFUSES RATHER THAN CLAMPS. A curve that earned nothing over its whole tail has no
   * rate to be carried at, and the honest answer is to say so - returning the horizon as
   * though it were an answer is the exact defect this function exists to remove.
   */
  let tailPerTick = 0;
  function tailRatePerTick(): number {
    if (tailPerTick > 0) return tailPerTick;
    ensureTicks(maxTicks);
    const n = measuredEnd();
    // NOT named `window`: no-steering.test.ts forbids that identifier anywhere in this
    // directory, because the voyage layer is pure and has no browser host. The fence
    // caught it on the first run of this fix, which is the fence doing its job.
    const tailTicks = Math.min(Math.max(1, Math.round(TAIL_RATE_WINDOW_HOURS * TICKS_PER_HOUR)), n);
    const gained = tailTicks > 0 ? (xpAfter[n] ?? 0) - (xpAfter[n - tailTicks] ?? 0) : 0;
    if (!(tailTicks > 0) || !(gained > 0)) {
      throw new Error(
        `voyage: curve '${curveId}' has no rate over the last ${tailTicks} ticks of the ` +
          `${measuredHours} h it was measured across, so there is nothing to carry it past that. ` +
          `A curve asked for a moment it cannot reach refuses; it never answers with its own horizon.`,
      );
    }
    tailPerTick = gained / tailTicks;
    return tailPerTick;
  }

  function progressAtTicks(ticks: number): Progress {
    if (!Number.isFinite(ticks) || ticks <= 0) return 0;
    if (ticks > maxTicks) {
      ensureTicks(maxTicks);
      return (xpAfter[maxTicks] ?? 0) + (ticks - maxTicks) * tailRatePerTick();
    }
    const t = Math.floor(ticks);
    ensureTicks(t);
    return xpAfter[t] ?? 0;
  }

  return {
    id: curveId,
    horizonHours,

    progressAtHours(hours: number): Progress {
      return progressAtTicks((Number.isFinite(hours) ? hours : 0) * TICKS_PER_HOUR);
    },

    /**
     * The first moment at which a continuously-present player holds this much
     * progression. Monotone, and exact to the tick rather than interpolated, because an
     * interpolated inverse would disagree with the forward direction at every step edge.
     *
     * TOTAL. There is no progression it cannot answer for. Inside the measured table the
     * answer is a tick index; past it the answer is the table's own terminal rate carried
     * forward. The one thing it will not do is return its own horizon.
     */
    hoursAtProgress(progress: Progress): number {
      if (!Number.isFinite(progress) || progress <= 0) return 0;
      // Grow the table geometrically until it brackets the answer, rather than building
      // it out to the horizon on the first query. A full 400 h table is 1.44 M ticks;
      // paying that to answer "how long is 30 h" would make the inverse the expensive
      // direction for no reason.
      let want = Math.max(TICKS_PER_HOUR, measuredEnd() + 1);
      ensureTicks(want);
      while ((xpAfter[measuredEnd()] ?? 0) < progress && measuredEnd() < maxTicks) {
        want = Math.min(want * 2, maxTicks);
        ensureTicks(want);
      }
      const last = xpAfter[measuredEnd()] ?? 0;
      if (progress > last) {
        // PAST THE MEASURED TABLE. Carried at the terminal rate, and never answered with
        // the horizon - CL4-C-01. `xpAfter` is at exactly `maxTicks` here, because that is
        // the only condition the loop above can have exited on, so this answer is a pure
        // function of (seed, skill, species, progress) and of nothing a caller did first.
        return (maxTicks + (progress - last) / tailRatePerTick()) / TICKS_PER_HOUR;
      }
      let lo = 0;
      let hi = measuredEnd();
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if ((xpAfter[mid] ?? 0) < progress) lo = mid + 1;
        else hi = mid;
      }
      return lo / TICKS_PER_HOUR;
    },
  };
}

export interface SimProgressSource {
  readonly curveId: string;
  progressAt(timestampMs: Millis): Progress;
  /** See `ProgressSource.paidHoursAt` in voyage.ts. The save's own clock, in hours. */
  paidHoursAt(timestampMs: Millis): number;
  /**
   * The same clock in TICKS, exact: an integer, so a surface that needs milliseconds
   * multiplies by TICK_MS rather than round-tripping hours through a float. session.ts's
   * hold view reads this so that `paidMs + skippedMs` is the elapsed wall-clock to the
   * tick, which paid-clock.test.ts asserts with no tolerance.
   */
  paidTicksAt(timestampMs: Millis): number;
}

/**
 * THE DEFINITION of what a settled save has earned, written the obvious way: one call to
 * the production `advance`, which is the same call the app makes to move time forward.
 *
 * `simProgressSource` is a memoised implementation of exactly this function and is
 * asserted equal to it in sim-bridge.test.ts. Keeping the slow, obvious version exported
 * is what makes the fast one auditable: an optimisation with no reference to be checked
 * against is a second rate engine wearing a cache's clothes.
 */
export function settledProgressAt(state: SimState, timestampMs: Millis): Progress {
  if (!Number.isFinite(timestampMs)) return progressScalar(state);
  return progressScalar(advance(state, timestampMs));
}

/** Wall-clock ms at which a tick begins, for callers pinning a sample to a tick edge. */
export function msAtSimTick(state: SimState, tick: number): Millis {
  return msAtTick(state.epochMs, tick);
}

/**
 * The live driver: a persisted save settled from timestamps through the production
 * `advance`, which is the same call the app makes whether the tab was focused, throttled
 * or closed. Pure in `timestampMs` for a fixed state, which is what the voyage layer's
 * ProgressSource contract requires.
 *
 * THE ANCHOR NEVER MOVES. The state handed in is never mutated and never replaced, so
 * every answer is a function of (that state, that timestamp) and of nothing else. That is
 * how "watched and unwatched settle identically" is true here rather than approximately
 * true.
 *
 * -- THE TABLE, AND WHY IT CANNOT CHANGE AN ANSWER ---------------------------
 *
 * `advance(state, t)` runs the tick indices [state.tick, state.tick + min(gap, hold)) in
 * order and nothing else, and `stepTick` is a pure function of (state, index). So the
 * progression after any absence is `paid[min(gap, hold)]`, where `paid[i]` is the state
 * after running i of those indices from the anchor - one monotone table, grown on demand,
 * capped at the hold because nothing past the hold is ever paid.
 *
 * A cursor that only moves forward is NOT enough, and the version of this file that had
 * one was thirty seconds slower per test file: a query for an earlier moment fell back to
 * the anchor and re-ran the whole absence, so a loop sampling backwards was quadratic. A
 * table answers any order in O(1) and cannot answer two different things for one instant.
 *
 * The hold is applied to the LOOKUP INDEX, so it is still enforced exactly once and the
 * table can never hand a player anything past it. `sim-bridge.test.ts` asserts this source
 * equals a fresh `advance(state, t).xp` at samples on both sides of the hold, in shuffled
 * order, which is what makes the table an optimisation rather than a second rate engine.
 *
 * -- AND BACKWARDS, WHICH IS THE ROUND-9 FIX ---------------------------------
 *
 * This source used to answer `progressScalar(state)` for EVERY instant at or before the
 * anchor's cursor, and `paidTicksAt` clamped the same way through `previewAbsence`. That
 * made `view(t)` a function of when it was asked rather than of `t`: a tab that reloaded
 * at 12:00 reported the 12:00 boat for 11:00, while the tab that never closed reported
 * the 11:00 boat, on one save with one seed. MEASURED in a real browser over four loads
 * (gauntlet/spike/reload/manifest.json): identical seed, epoch, skippedTicks and open
 * notes, four different positions, and on every reloaded row `view(T0)` equalled
 * `view(cursor)` to every printed digit.
 *
 * The clamp was a shortcut, not a necessity. `stepTick` is pure in (state, index) and
 * `advance` runs consecutive indices from the cursor, so the past is replayable from any
 * state a caller can VOUCH FOR - and vouching is the whole of `rewindFor` below.
 */

/**
 * Ticks the rewind will replay from a save's OWN EPOCH before it declines to.
 *
 * Tied to the curve's measured horizon because that is the table the rewind reads: every
 * world already builds this identity's continuous table to exactly this depth, so a
 * rewind inside it costs nothing that was not already being spent, and past it the rewind
 * would be allocating a table nothing else in the program wants. A save deeper than this
 * still rewinds through the near base below, which is what the renderer actually asks for.
 */
export const MAX_REWIND_TICKS = Math.round(DEFAULT_SIM_CURVE_HORIZON_HOURS * TICKS_PER_HOUR);

/** One way of answering for an instant BEFORE the anchor's cursor. */
interface Rewind {
  /** The earliest absolute tick index this base can answer for. */
  readonly fromTick: number;
  progressAt(targetTick: number): Progress;
  paidTicksAt(targetTick: number): number;
}

/** Two states are the same run when nothing that shapes the replay differs. */
function sameRun(a: SimState, b: SimState): boolean {
  return a.seed === b.seed && a.epochMs === b.epochMs && a.skill === b.skill;
}

/**
 * THE NEAR BASE: a state the CALLER holds from earlier on this same run.
 *
 * A session hands in the state it opened with. Between that state and the anchor the
 * session did the advancing itself, so the paid indices are contiguous from the base's
 * cursor and replaying them reproduces exactly the states it held - PROVIDED nothing was
 * skipped in between, which is what the `skippedTicks` equality tests. A hold crossing
 * would make the span path-dependent (three 12 h gaps pay 36 h; one 36 h gap pays 24 h
 * and skips 12), and that is precisely the case this refuses.
 *
 * Its cost is bounded by time-since-open, and its table is memoised on the base OBJECT,
 * so a session that hands in the same state every tick pays for each tick once rather
 * than once per frame.
 */
const tablesByBase = new WeakMap<SimState, ReplayTable>();

function nearRewind(state: SimState, offered: SimState | undefined): Rewind | null {
  if (offered === undefined) return null;
  if (!sameRun(offered, state)) return null;
  if (!Number.isFinite(offered.tick) || offered.tick > state.tick) return null;
  if (offered.skippedTicks !== state.skippedTicks) return null;
  const basePaid = anchorPaidTicks(offered);
  if (basePaid === null) return null;
  let table = tablesByBase.get(offered);
  if (table === undefined) {
    table = replayFrom(offered);
    tablesByBase.set(offered, table);
  }
  const from = offered.tick;
  const built = table;
  return {
    fromTick: from,
    progressAt: (targetTick) => built.at(targetTick - from),
    paidTicksAt: (targetTick) => basePaid + (targetTick - from),
  };
}

/**
 * THE FAR BASE: the save's own epoch.
 *
 * A save that has never skipped a tick IS the continuous replay of its own epoch - the
 * indices run are exactly [0, tick) - so the whole of its past is readable off the shared
 * table, including instants from before the tab asking was ever opened. That is the case
 * the four browser loads were in, and it is the case any save is in until its first
 * absence past the hold.
 *
 * The bound is taken on the CURSOR rather than on the instant asked for, so whether this
 * base exists is a fact about the save and never about the query.
 *
 * TARGET CHOICE DOES NOT DISTURB IT. The table is built with the anchor's current target,
 * which may not be the target the save was played under - and `targeting.test.ts` asserts
 * with zero tolerance that xp, coins, junk and specials are identical across every target
 * on one seed, because nothing on the tempo path reads the choice. Only `catches` and
 * `weightG` move, and the boat reads neither. Skill would disturb it and cannot change:
 * there is no setter for it anywhere in the build.
 */
function farRewind(state: SimState, curveId: string): Rewind | null {
  if (state.skippedTicks !== 0) return null;
  if (!Number.isFinite(state.tick) || state.tick < 0 || state.tick > MAX_REWIND_TICKS) return null;
  const table = continuousReplay(curveId, state.seed, state.skill, state.targetSpecies);
  // IT HAS TO REPRODUCE THE ANCHOR, OR IT IS NOT THIS SAVE'S PAST. A state whose
  // progression is not the replay of its own cursor did not get there by playing - a
  // hand-edited save, a loader clamp, or a fence's synthetic deep-time fixture - and
  // rewinding one would draw a boat that jumps at the cursor. It costs nothing
  // asymptotically: any backward query reaches for an index near the cursor anyway, so
  // this only moves that growth from the first query to the source's construction.
  if (table.at(state.tick) !== progressScalar(state)) return null;
  return {
    fromTick: 0,
    progressAt: (targetTick) => table.at(targetTick),
    paidTicksAt: (targetTick) => targetTick,
  };
}

export interface SimProgressSourceOptions extends Partial<SimCurveOptions> {
  /**
   * A state from EARLIER ON THIS SAME RUN that the caller can vouch for - in practice the
   * state a session opened with. It is what lets a save that has skipped time still
   * answer for every instant since the tab opened, which is every instant the renderer
   * draws. Never persisted, never advanced, and never trusted beyond the checks in
   * `nearRewind`.
   */
  readonly rewindBase?: SimState;
}

export function simProgressSource(state: SimState, options: SimProgressSourceOptions = {}): SimProgressSource {
  const { rewindBase, ...curveOptions } = options;
  const curveId = simCurveIdFor({
    seed: state.seed,
    skill: state.skill,
    targetSpecies: state.targetSpecies,
    ...curveOptions,
  });

  // paid[i] is the progression after i ticks of absence have been paid from the anchor.
  const paid: number[] = [progressScalar(state)];
  let cursor: SimState = state;

  function ensurePaid(ticks: number): void {
    const want = Math.min(Math.max(0, ticks), OFFLINE_HOLD_TICKS);
    while (paid.length <= want) {
      cursor = stepTick(cursor, cursor.tick);
      paid.push(progressScalar(cursor));
    }
  }

  // THE TWO BASES, BUILT ONCE PER SOURCE AND NEITHER OF THEM EAGER. Constructing either
  // runs no ticks: `nearRewind` only looks its table up, `farRewind` only looks the shared
  // one up, and both grow on the first query that reaches them.
  const near = nearRewind(state, rewindBase);
  const far = farRewind(state, curveId);

  /**
   * How this source answers for an instant STRICTLY BEFORE the cursor, or null when it
   * cannot and the anchor's own progression is the honest answer.
   *
   * The near base first, because it is the cheaper replay and the one a skipped save has;
   * the far base under it, because only it reaches instants from before this tab opened.
   * Where both apply the save never skipped, so both are exact and they agree - which
   * `sim-bridge.test.ts` asserts rather than assumes.
   */
  function rewindTo(targetTick: number): Rewind | null {
    if (!Number.isFinite(targetTick) || targetTick >= state.tick) return null;
    if (near !== null && targetTick >= near.fromTick) return near;
    if (far !== null && targetTick >= far.fromTick) return far;
    return null;
  }

  /**
   * Ticks of play this save has been paid for at `timestampMs`: the anchor's PAID ticks
   * (see anchorPaidTicks - NOT its cursor, which is elapsed time) plus whatever of this
   * absence the hold pays, split by sim-core's own `previewAbsence` so there is one
   * implementation of the hold and this file does not carry a second.
   *
   * Presence-invariant by construction: settling moves ticks from the pending term into
   * the anchor term and changes the sum by nothing, so `view()` and `tick()` at one
   * `nowMs` land on one number. Saturation is tested on the SUM of banked and pending skip,
   * so the instant an absence would saturate the counter, the un-ticked view refuses too.
   */
  function paidTicksAt(timestampMs: Millis): number {
    const anchor = anchorPaidTicks(state);
    if (anchor === null) return 0;
    if (!Number.isFinite(timestampMs)) return Math.max(0, anchor);
    const target = tickAtMs(state.epochMs, timestampMs);
    const back = rewindTo(target);
    // The counter that refuses forward refuses backward too: `anchorPaidTicks` already
    // returned null above at saturation, so there is no second test to keep level here.
    if (back !== null) return Math.max(0, back.paidTicksAt(target));
    const now = previewAbsence(state, timestampMs);
    if (state.skippedTicks + now.skippedTicks >= MAX_COUNT) return 0;
    return Math.max(0, anchor + now.paidTicks);
  }

  return {
    curveId,
    progressAt(timestampMs: Millis): Progress {
      if (!Number.isFinite(timestampMs)) return progressScalar(state);
      const target = tickAtMs(state.epochMs, timestampMs);
      const gap = target - state.tick;
      if (gap < 0) {
        const back = rewindTo(target);
        return back === null ? progressScalar(state) : back.progressAt(target);
      }
      if (gap === 0) return progressScalar(state);
      const index = Math.min(gap, OFFLINE_HOLD_TICKS);
      ensurePaid(index);
      // WHAT THIS LINE THROWS AWAY IS THE FIX TO A HIGH FINDING. `index` is unbounded
      // playtime; `paid[index]` is XP, which saturates at MAX_XP = 200,000,000 and takes
      // the whole settled world constant with it. The bounded lookup was the only thing
      // this source published, so the voyage layer had no unbounded clock to pace a round
      // with. `paidHoursAt` publishes the other half.
      return paid[index] ?? progressScalar(state);
    },
    paidHoursAt(timestampMs: Millis): number {
      return (paidTicksAt(timestampMs) * TICK_MS) / 3_600_000;
    },
    paidTicksAt,
  };
}

/**
 * The id `simProgressionCurve` would produce for these options.
 *
 * It takes the same REQUIRED seed, because a mirror with a default of its own is how the
 * two sides of the guard came to disagree in the first place.
 */
export function simCurveIdFor(options: SimCurveOptions): string {
  const seed = options.seed;
  const skill = options.skill ?? 0;
  const species = options.targetSpecies ?? '';
  return `sim-xp:seed=${seed}:skill=${skill}:species=${species === '' ? 'best' : species}`;
}

/** A fresh save anchored at `epochMs`, matching a curve built with the same options. */
export function freshSimState(epochMs: Millis, options: SimCurveOptions): SimState {
  return {
    ...createState(options.seed, epochMs),
    skill: options.skill ?? 0,
    targetSpecies: options.targetSpecies ?? '',
  };
}

/**
 * Where a settled save stops agreeing with the continuous-play curve. It is exactly the
 * hold, and the hold is the owner's 24 hours.
 *
 * ITS DOCSTRING USED TO CLAIM IT "REPORTS WHERE THAT HAPPENS RATHER THAN HIDING IT", which
 * round 3's critic correctly called out: it reports the hold's LENGTH, which is a constant,
 * and no player ever saw it (CL3-C-04). The disclosure a player can actually read is
 * `VoyageView.hold`, in session.ts, which carries how much of THIS save's time was skipped.
 * This constant is the shared number both sides derive from, and nothing more.
 */
export const holdDivergenceHours: number = OFFLINE_HOLD_HOURS;

// ---------------------------------------------------------------------------
// The world the game actually sails.
// ---------------------------------------------------------------------------

export interface SimVoyageWorldOptions extends SimCurveOptions {
  readonly places?: CreateVoyageWorldOptions['places'];
  readonly chart?: CreateVoyageWorldOptions['chart'];
  readonly firstLandfallHours?: number;
  readonly journeyLaw?: CreateVoyageWorldOptions['journeyLaw'];
}

/**
 * A voyage world paced against the REAL simulation. This is the world the game sails and
 * the only one whose hour figures mean anything.
 */
export function createSimVoyageWorld(options: SimVoyageWorldOptions): VoyageWorld {
  return createVoyageWorld({
    curve: simProgressionCurve(options),
    ...(options.places === undefined ? {} : { places: options.places }),
    ...(options.chart === undefined ? {} : { chart: options.chart }),
    ...(options.firstLandfallHours === undefined ? {} : { firstLandfallHours: options.firstLandfallHours }),
    ...(options.journeyLaw === undefined ? {} : { journeyLaw: options.journeyLaw }),
  });
}

/**
 * THE WORLD A PARTICULAR SAVE SAILS, memoised by the curve that save realises.
 *
 * This replaces round 2's `simVoyageWorld()`, a no-argument memoised singleton documented
 * as "the world the game sails" that could only ever realise seed 1 (CL2-R2-F). Every
 * real save picks its own seed - the dev harness uses 4242, `createSave(seed, nowMs)`
 * takes one - so the singleton was a world that was wrong for every save except a test
 * fixture's, and pairing it with a real save's progress source threw at the first resolve
 * with "The sea would be the wrong size."
 *
 * The shape of the fix is the point: the world is now derived FROM THE SAVE, so the two
 * arguments of `assertSourceMatchesWorld` are produced by one input instead of two
 * defaults. There is no configuration in which a caller can hold a world and a source
 * that disagree, because there is no way to name a world except by naming the save.
 *
 * Memoised because building the curve costs a simulation run; keyed by curve id because
 * that is exactly the identity the guard compares. The cache is bounded: a session holds
 * one save, and an import replaces it.
 */
const worldsByCurveId = new Map<string, VoyageWorld>();

/** Most distinct saves one page will ever open. Past this the oldest entry is dropped. */
const MAX_MEMOISED_WORLDS = 8;

export function simVoyageWorldFor(state: SimState, options: Partial<SimCurveOptions> = {}): VoyageWorld {
  const identity: SimCurveOptions = {
    seed: state.seed,
    skill: state.skill,
    targetSpecies: state.targetSpecies,
    ...options,
  };
  const id = simCurveIdFor(identity);
  const hit = worldsByCurveId.get(id);
  if (hit !== undefined) return hit;
  const built = createSimVoyageWorld(identity);
  if (worldsByCurveId.size >= MAX_MEMOISED_WORLDS) {
    const oldest = worldsByCurveId.keys().next();
    if (!oldest.done) worldsByCurveId.delete(oldest.value);
  }
  worldsByCurveId.set(id, built);
  return built;
}
